import React, { useState, useEffect, useRef } from "react";
import { User, CreditCard, Shield, Bell, LogOut, Check, X, Globe, Plug, Copy, Download } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { API_BASE_URL } from "../config";
import { MARKETS, MARKET_CODES } from "../config/markets";

export default function SettingsPage() {
  const { user, logout } = useAuth();
  // AI apps is the default tab: most visitors come here for the connector details (user, 2026-10-23).
  // ?tab=account / ?tab=billing open the others directly.
  const [activeTab, setActiveTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return t === "account" || t === "billing" ? t : "ai-apps";
  });

  return (
    <div className="p-6 w-full mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Settings</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab("ai-apps")}
            className={`px-6 py-4 text-sm font-medium flex items-center gap-2 transition-colors ${
              activeTab === "ai-apps"
                ? "text-brand-cta border-b-2 border-brand-cta bg-green-50/50"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <Plug className="w-4 h-4" />
            AI apps
          </button>
          <button
            onClick={() => setActiveTab("account")}
            className={`px-6 py-4 text-sm font-medium flex items-center gap-2 transition-colors ${
              activeTab === "account"
                ? "text-brand-cta border-b-2 border-brand-cta bg-green-50/50"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <User className="w-4 h-4" />
            Account
          </button>
          <button
            onClick={() => setActiveTab("billing")}
            className={`px-6 py-4 text-sm font-medium flex items-center gap-2 transition-colors ${
              activeTab === "billing"
                ? "text-brand-cta border-b-2 border-brand-cta bg-green-50/50"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
            }`}
          >
            <CreditCard className="w-4 h-4" />
            Billing
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === "account" && <AccountSettings user={user} />}
          {activeTab === "billing" && <BillingSettings user={user} />}
          {activeTab === "ai-apps" && <AIAppsSettings />}
        </div>
      </div>
    </div>
  );
}

function AccountSettings({ user }) {
  return (
    <div className="space-y-8 max-w-xl">
      {/* Profile Section */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Information</h3>
        <div className="flex flex-col items-start gap-6">
          <div className="relative group ">
            <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center text-gray-400 overflow-hidden">
             {/* Mock Avatar */}
             <User className="w-12 h-12" />
            </div>
            <button className="absolute bottom-0 right-0 p-1.5 bg-brand-cta text-white rounded-full shadow-sm hover:bg-emerald-600 transition-colors">
              <span className="sr-only">Change Photo</span>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
          
          <div className="flex-1 space-y-4 w-full">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input 
                type="text" 
                defaultValue={user?.first_name ? `${user.first_name} ${user.last_name || ''}`.trim() : "User"} 
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cta focus:border-brand-cta outline-none transition-all"
              />
            </div>
             <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input 
                type="email" 
                defaultValue={user?.email || ""} 
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
              />
            </div>
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* Primary market (plan 224) */}
      <MarketSettings user={user} />

      <hr className="border-gray-100" />

      {/* Password Section */}
      {/* <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-gray-500" />
          Security
        </h3>
        <div className="w-full space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input 
              type="password" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cta focus:border-brand-cta outline-none transition-all"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input 
              type="password" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cta focus:border-brand-cta outline-none transition-all"
              placeholder="••••••••"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input 
              type="password" 
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cta focus:border-brand-cta outline-none transition-all"
              placeholder="••••••••"
            />
          </div>
          <div className="pt-2">
            <button className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium text-sm">
              Update Password
            </button>
          </div>
        </div>
      </section> */}
    </div>
  );
}

// plan 224: pick the primary market/country. Drives the AI agent's home-market
// context and the chart's default starting stock.
function MarketSettings({ user }) {
  const { checkAuthStatus } = useAuth();
  const current = user?.profile?.primary_market || "";
  const [market, setMarket] = useState(current);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { setMarket(user?.profile?.primary_market || ""); }, [user]);

  const dirty = market !== current;

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${API_BASE_URL}/api/accounts/me/`,
        { primary_market: market },
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      await checkAuthStatus(); // refresh user so the chart + agent pick it up
      setSaved(true);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
        <Globe className="w-5 h-5 text-gray-500" />
        Primary market
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        Your home market. The AI assistant defaults to it for ambiguous tickers and suggestions,
        and the chart opens on a stock from this market.
      </p>
      <div className="flex items-end gap-3 max-w-md">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Country / market</label>
          <select
            value={market}
            onChange={(e) => { setMarket(e.target.value); setSaved(false); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-cta focus:border-brand-cta outline-none transition-all bg-white"
          >
            {MARKET_CODES.map((code) => (
              <option key={code} value={code}>
                {MARKETS[code].label} — {MARKETS[code].exchange}
              </option>
            ))}
          </select>
        </div>
        <button
          onClick={handleSave}
          disabled={!dirty || saving}
          className="px-4 py-2 bg-brand-cta text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      {saved && (
        <p className="text-sm text-emerald-600 mt-2 flex items-center gap-1">
          <Check className="w-4 h-4" /> Saved
        </p>
      )}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </section>
  );
}

function BillingSettings({ user }) {
  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [subResponse, historyResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/payments/subscription/manage/`),
          axios.get(`${API_BASE_URL}/api/payments/subscription/billing-info/`)
        ]);

        // Subscription Logic
        let activeSub = subResponse.data.find(sub => sub.status === 'active' || sub.status === 'trialing');
        if (!activeSub) {
           activeSub = subResponse.data.find(sub => sub.status === 'canceled' && new Date(sub.current_period_end) > new Date());
        }
        setSubscription(activeSub || null);

        // Billing History Logic
        setBillingHistory(historyResponse.data || []);

      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCancelSubscription = async () => {
    if (!subscription) return;
    
    if (!window.confirm("Are you sure you want to cancel your subscription? You will lose access to premium features at the end of your current billing period.")) {
      return;
    }

    setCancelling(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/api/payments/subscription/manage/`, {
        event: "cancel",
        subscription_id: subscription.id,
        at_end_period: true
      });

      if (response.data.status === "canceled") {
        setSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      alert("Failed to cancel subscription. Please try again or contact support.");
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-cta"></div>
      </div>
    );
  }

  const isPremium = !!subscription && (subscription.status === 'active' || subscription.status === 'trialing' || (subscription.status === 'canceled' && new Date(subscription.current_period_end) > new Date()));
  const isCanceled = subscription?.status === 'canceled' || subscription?.cancel_at_period_end;
  
  const planName = subscription ? subscription.plan.name : "Free Plan";
  const planPrice = subscription ? `${subscription.plan.currency.toUpperCase()} ${subscription.plan.price}` : "$0.00";
  const billingInterval = subscription ? subscription.plan.billing_interval : "";
  const nextBillingDate = subscription ? new Date(subscription.current_period_end).toLocaleDateString() : "";

  return (
    <div className="space-y-8">
      {/* Current Plan */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Subscription Plan</h3>
        <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h4 className="text-xl font-bold text-gray-900">{planName}</h4>
                {isPremium ? (
                   <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${isCanceled ? 'bg-yellow-100 text-yellow-800' : 'bg-emerald-100 text-emerald-800'}`}>
                     {isCanceled ? (
                       <>
                        <LogOut className="w-3 h-3" /> Canceling
                       </>
                     ) : (
                       <>
                        <Check className="w-3 h-3" /> Active
                       </>
                     )}
                   </span>
                ) : (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                    Free
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600">
                {isPremium 
                  ? (isCanceled 
                      ? `Your subscription has been canceled and will end on ${nextBillingDate}.`
                      : `Billed ${billingInterval}. Next billing date: ${nextBillingDate}. Amount: ${planPrice}`)
                  : "You're on the free plan."}
              </p>
            </div>
             <div className="flex items-center gap-3">
               
               {isPremium && !isCanceled && (
                 <button 
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="px-4 py-2 border border-red-200 text-red-600 rounded-lg font-medium hover:bg-red-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                 >
                   {cancelling ? "Cancelling..." : "Cancel Subscription"}
                 </button>
               )}
             </div>
          </div>
        </div>
      </section>

      <hr className="border-gray-100" />

      {/* Billing History */}
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Billing History</h3>
        <div className="overflow-hidden rounded-lg border border-gray-200">
          {billingHistory.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {billingHistory.map((item) => (
                  <tr key={item.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(item.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.currency.toUpperCase()} {item.total}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right">
                      {item.invoice_pdf && (
                        <a 
                          href={item.invoice_pdf} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-brand-cta hover:text-emerald-700 font-medium hover:underline"
                        >
                          Download
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-8 text-center text-gray-500 text-sm">
              No billing history available.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

// Web MCP (plan 282): connect AnalystBro to Gemini, Claude or ChatGPT on the web.
// The user pastes the MCP URL plus their own Client ID and secret into the app's
// connector form; the app then sends them back here to approve read-only access.
// What to type into an AI app's "add connector" form (ChatGPT asks for a name, description and icon).
const CONNECTOR_NAME = "AnalystBro";
const CONNECTOR_DESCRIPTION =
  "Live market data and company research for Bangladesh (DSE) and global stocks: prices, charts, financial " +
  "statements, dividends, screening, and searchable annual and quarterly reports with links to the full PDFs on " +
  "analystbro.net.";

function CopyField({ label, value, secret, multiline }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <div className="flex gap-2">
        {multiline ? (
          <textarea
            readOnly
            value={value}
            rows={3}
            className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm resize-none"
            onFocus={(e) => e.target.select()}
          />
        ) : (
          <input
            readOnly
            value={value}
            className={`flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm font-mono ${secret ? "text-amber-800" : ""}`}
            onFocus={(e) => e.target.select()}
          />
        )}
        <button
          type="button"
          onClick={copy}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1"
          title={`Copy ${label}`}
        >
          {copied ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

// Plan 282c: every AI app that can use AnalystBro for this user (own Client ID, apps connected by
// URL, directory listings). Disconnect ends only this user's access for that one app.
function formatWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

function ConnectedApps() {
  const [apps, setApps] = useState(null);
  const [error, setError] = useState("");
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(null);
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const base = `${API_BASE_URL}/api/mcp/web/connected-apps/`;

  const load = () =>
    axios.get(base, auth())
      .then(({ data }) => setApps(data.apps || []))
      .catch(() => { setError("Could not load your connected apps."); setApps([]); });
  useEffect(() => { load(); }, []);

  const disconnect = async (app) => {
    setBusy(app.id); setError("");
    try {
      await axios.post(`${base}${app.id}/disconnect/`, {}, auth());
      setApps((list) => list.filter((a) => a.id !== app.id));
    } catch { setError(`Could not disconnect ${app.name}. Try again.`); }
    setBusy(null); setConfirming(null);
  };

  return (
    <section>
      <h4 className="font-medium text-gray-800 mb-1">Connected apps</h4>
      <p className="text-sm text-gray-500 mb-3">
        These apps can use AnalystBro for you right now. Disconnect one to stop it at once; it will need to
        connect again before it can use AnalystBro.
      </p>
      {apps === null ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : apps.length === 0 ? (
        <p className="text-sm text-gray-500 border border-gray-200 rounded-lg px-3 py-3">No apps are connected.</p>
      ) : (
        <ul className="divide-y divide-gray-200 border border-gray-200 rounded-lg">
          {apps.map((app) => (
            <li key={app.id} className="px-3 py-3 flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium text-gray-800 break-words">{app.name}</div>
                <div className="text-xs text-gray-500">
                  Connected {formatWhen(app.connected_at)} · Last active {formatWhen(app.last_used_at)}
                </div>
              </div>
              {confirming === app.id ? (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-700">Disconnect {app.name}?</span>
                  <button
                    onClick={() => disconnect(app)}
                    disabled={busy === app.id}
                    className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50"
                  >
                    {busy === app.id ? "Disconnecting…" : "Yes, disconnect"}
                  </button>
                  <button
                    onClick={() => setConfirming(null)}
                    disabled={busy === app.id}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirming(app.id)}
                  className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
                >
                  Disconnect
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
    </section>
  );
}

function AIAppsSettings() {
  const [state, setState] = useState({ loading: true, mcpUrl: "", credential: null, secret: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
  const url = `${API_BASE_URL}/api/mcp/web/credentials/`;

  useEffect(() => {
    axios.get(url, auth())
      .then(({ data }) => setState({ loading: false, mcpUrl: data.mcp_url, credential: data.credential, secret: data.credential?.client_secret || "" }))
      .catch(() => { setError("Could not load your AI app connection."); setState((s) => ({ ...s, loading: false })); });
  }, []);

  const create = async () => {
    if (state.credential && !window.confirm("Rotate your Client ID and secret?\n\nEVERY AI app you connected (Gemini, Claude, ChatGPT) will STOP WORKING right away. To use them again you must enter the new Client ID and secret in each one.\n\nOnly do this if you think the secret leaked.")) return;
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(url, {}, auth());
      setState({ loading: false, mcpUrl: data.mcp_url, credential: data.credential, secret: data.credential.client_secret });
    } catch { setError("Could not create the credentials. Try again."); }
    setBusy(false);
  };

  return (
    <div className="space-y-6 max-w-2xl">
      <section>
        <h3 className="text-lg font-semibold text-gray-800 mb-1 flex items-center gap-2">
          <Plug className="w-5 h-5 text-gray-500" />
          Use AnalystBro in Gemini, Claude or ChatGPT
        </h3>
        <p className="text-sm text-gray-500">
          Add AnalystBro as a connector and ask about prices, charts, filings and screens there. It gives market
          data only, read-only. Your plan and limits are the same as in the app.
        </p>
      </section>

      {state.loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <>
          <section className="space-y-3">
            <CopyField label="MCP server URL" value={state.mcpUrl} />
            {state.credential && <CopyField label="Client ID" value={state.credential.client_id} />}
            {state.secret ? (
              <>
                <CopyField label="Client secret" value={state.secret} secret />
                <p className="text-sm text-gray-500">
                  Use this same Client ID and secret for every AI app. Keep the secret private.
                </p>
              </>
            ) : state.credential ? (
              <p className="text-sm text-amber-700">
                This credential was made before secrets could be shown again. Rotate it once to see the secret.
              </p>
            ) : null}
            <div className="flex gap-3 pt-1">
              <button
                onClick={create}
                disabled={busy}
                className="px-4 py-2 bg-brand-cta text-white rounded-lg hover:bg-emerald-600 transition-colors font-medium text-sm disabled:opacity-50"
              >
                {state.credential ? "Rotate secret" : "Create credentials"}
              </button>
            </div>
            {state.credential && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <b>Rotating stops every AI app you connected.</b> Gemini, Claude and ChatGPT lose access right away,
                and you will have to enter the new Client ID and secret in each of them. Only rotate if you think the
                secret leaked. To stop just one app, use Disconnect under Connected apps.
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
          </section>

          <ConnectedApps />

          <section>
            <h4 className="font-medium text-gray-800 mb-2">Add it to Gemini</h4>
            <ol className="list-decimal pl-5 text-sm text-gray-600 space-y-1">
              <li>In Gemini, open <b>Customize → Connectors → + Custom → Connect to an MCP server</b>.</li>
              <li>Paste the MCP server URL. Under <b>Additional settings</b>, paste the Client ID and Client secret.</li>
              <li>Sign in to AnalystBro when asked and click <b>Allow</b>.</li>
            </ol>
            <p className="text-sm text-gray-500 mt-3">
              ChatGPT asks you to upload an icon for the app.{" "}
              <a
                href="/images/analystbro-logo-connector.png"
                download="analystbro-logo.png"
                className="inline-flex items-center gap-1 text-brand-cta hover:underline font-medium"
              >
                <Download className="w-4 h-4" />
                Download the AnalystBro logo
              </a>{" "}
              (PNG, 256×256, under 10 KB).
            </p>
            <div className="mt-3 space-y-3">
              <p className="text-sm text-gray-500">It also asks for a name and a description. Copy these:</p>
              <CopyField label="Name" value={CONNECTOR_NAME} />
              <CopyField label="Description" value={CONNECTOR_DESCRIPTION} multiline />
            </div>
            <p className="text-sm text-gray-500 mt-3">
              Claude and ChatGPT work the same way: add a custom connector with the URL, and the Client ID and secret
              under advanced settings.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
